import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  ArrowForward,
  CheckCircleOutline,
  HourglassEmpty,
  ErrorOutline,
} from '@mui/icons-material';
import { TopicRecommendation, DisplayChip } from '../../services/recommendationService';

const CHIP_CONFIG: Record<
  DisplayChip,
  { label: string; color: string; bg: string; icon: React.ReactNode }
> = {
  READY: {
    label: 'Ready',
    color: '#00b894',
    bg: 'rgba(0, 184, 148, 0.1)',
    icon: <CheckCircleOutline fontSize="small" />,
  },
  IN_PROGRESS: {
    label: 'In Progress',
    color: '#0984e3',
    bg: 'rgba(9, 132, 227, 0.1)',
    icon: <HourglassEmpty fontSize="small" />,
  },
  STRUGGLING: {
    label: 'Struggling',
    color: '#e17055',
    bg: 'rgba(225, 112, 85, 0.1)',
    icon: <ErrorOutline fontSize="small" />,
  },
  PREREQUISITE_GAP: {
    label: 'Prerequisite Gap',
    color: '#d63031',
    bg: 'rgba(214, 48, 49, 0.1)',
    icon: <ErrorOutline fontSize="small" />,
  },
};

interface MissionCardProps {
  rec: TopicRecommendation;
  onClick: () => void;
}

/**
 * Recommendation styled as a "mission briefing" card with a thick coloured
 * left border, a status chip, and a hover slide-right effect.
 */
const MissionCard: React.FC<MissionCardProps> = ({ rec, onClick }) => {
  const chip = CHIP_CONFIG[rec.displayChip as DisplayChip] ?? CHIP_CONFIG.READY;

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2,
        borderRadius: 3,
        borderLeft: `4px solid ${chip.color}`,
        border: '1px solid rgba(0,0,0,0.06)',
        borderLeftWidth: 4,
        borderLeftColor: chip.color,
        cursor: 'pointer',
        transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
        '&:hover': {
          transform: 'translateX(5px)',
          boxShadow: `0 4px 20px ${chip.color}28`,
          borderColor: `${chip.color}55`,
          borderLeftColor: chip.color,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: chip.bg,
            borderRadius: 1.5,
            px: 1,
            py: 0.25,
            color: chip.color,
          }}
        >
          {chip.icon}
          <Typography variant="caption" fontWeight="bold">{chip.label}</Typography>
        </Box>
        <ArrowForward sx={{ fontSize: 15, color: chip.color, opacity: 0.6, mt: 0.5 }} />
      </Box>

      <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.2, lineHeight: 1.4 }}>
        {rec.topicName}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {rec.courseName}
      </Typography>
      {rec.reason && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontStyle: 'italic', display: 'block', mt: 0.5, lineHeight: 1.4 }}
        >
          {rec.reason}
        </Typography>
      )}

      {rec.lastQuizScore !== null && (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
            <Typography variant="caption" color="text.secondary">Last score</Typography>
            <Typography
              variant="caption"
              fontWeight="bold"
              sx={{ color: rec.lastQuizScore >= rec.passThreshold ? '#00b894' : '#e17055' }}
            >
              {rec.lastQuizScore.toFixed(0)}% / {rec.passThreshold}%
            </Typography>
          </Box>
          <Box sx={{ height: 3, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.07)', overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: `${Math.min(100, rec.lastQuizScore)}%`,
                bgcolor: rec.lastQuizScore >= rec.passThreshold ? '#00b894' : '#e17055',
                borderRadius: 2,
                transition: 'width 0.8s ease',
              }}
            />
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default MissionCard;
